import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import classnames from 'classnames';
import {
  DurationChip,
  IconChip,
  IncentiveChip,
  Link,
  NotificationPin,
  SessionFormatChip,
} from '@app/core';
import {
  DevicesCategory,
  Participation,
  RequiredMediaSource,
  Session,
  StudyType,
} from '@app/api';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  globalContainer: {
    width: '100%',
    marginTop: theme.marginBase * 3,
  },
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.marginBase / 2,
    flexWrap: 'wrap',
    marginBottom: theme.marginBase,
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
  title: {
    ...theme.fonts.label,
    paddingLeft: theme.marginBase,
  },
}));

interface Props {
  className?: string;
  session: Session | undefined;
  focus?: boolean;
  participation?: Participation;
  numberOfNewMessages?: number;
}

export const IconsMeetingList = ({
  className,
  focus = true,
  participation,
  numberOfNewMessages,
  session,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!session && participation) {
    session = participation.session;
  }

  return (
    <div className={classnames(classes.globalContainer, className)}>
      <div className={classnames(classes.container)}>
        {participation && <IncentiveChip participation={participation} />}
        <DurationChip participation={participation} session={session} />
        {!!numberOfNewMessages && numberOfNewMessages > 0 && participation && (
          <Link href={`/p/${participation.id}?tab=messages`}>
            <IconChip
              icon="ri-chat-3-line"
              color="inkSecondary"
              label={numberOfNewMessages.toString()}
              alt="chat"
            >
              <NotificationPin />
            </IconChip>
          </Link>
        )}
      </div>
      <div className={classnames(classes.container)}>
        {session?.studyType === StudyType.MODERATED ? (
          <SessionFormatChip
            participation={participation}
            session={session}
            color={focus ? 'ink' : 'purple'}
            variant={focus ? 'plain' : 'std'}
          />
        ) : (
          <IconChip
            icon="ri-list-check-2"
            color={focus ? 'ink' : 'purple'}
            variant={focus ? 'plain' : 'std'}
            label={t('application.info.studyTypeList.UNMODERATED')}
          />
        )}

        {session?.allowedDevicesCategories?.includes(
          DevicesCategory.COMPUTER,
        ) && (
          <IconChip
            icon="ri-computer-line"
            color="inkSecondary"
            alt="Computer"
          />
        )}
        {session?.allowedDevicesCategories?.includes(
          DevicesCategory.TABLET,
        ) && (
          <IconChip icon="ri-tablet-line" color="inkSecondary" alt="Tablet" />
        )}
        {session?.allowedDevicesCategories?.includes(
          DevicesCategory.SMARTPHONE,
        ) && (
          <IconChip
            icon="ri-smartphone-line"
            color="inkSecondary"
            alt="Smartphone"
          />
        )}
        {session?.requiredMediaSource?.includes(RequiredMediaSource.VIDEO) && (
          <IconChip icon="ri-webcam-line" color="inkSecondary" alt="Webcam" />
        )}
        {session?.requiredMediaSource?.includes(RequiredMediaSource.AUDIO) && (
          <IconChip icon="ri-mic-line" color="inkSecondary" alt="Mic" />
        )}
        {session?.requiredMediaSource?.includes(
          RequiredMediaSource.SCREEN_SHARING,
        ) && <IconChip icon="ri-cast-line" color="inkSecondary" alt="Cast" />}
      </div>
    </div>
  );
};
